import React, { useState, useEffect } from "react";
import { Eye, ChevronRight, CircleCheck } from "lucide-react";
import Input from "./Input";
import Pagination from "../ui/Pagination";
import SkeletonPage from "./SkeletonPage ";
import { tailwindBgToHex } from "../Pages/utils/taskUtils";

export default function MyTask({
    projects = [],
    user,
    formatStatusLabel,
    getStatusColor,
    TaskEmployees,
    employees,
    onTaskClick,
    TaskPriority
}) {
    const [selectProjectFilter, setSelectProjectFilter] = useState("");
    const [selectEmployee, setSelectEmployee] = useState("");
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setCurrentPage(1);
    }, [search, selectProjectFilter, selectEmployee]);

    useEffect(() => {
        if (projects && projects.length >= 0) {
            setLoading(false);
        }
    }, [projects]);

    const allowedProjects = projects.filter((project) => {
        if (["Admin", "Management"].includes(user?.department)) {
            return true;
        }
        const isShared = project.sharedWith?.includes(user?.userId);
        const isCreator = project.createdBy?.userId === user?.userId;
        return isShared || isCreator;
    });

    function getAllTasks(project, parentProjectId = null) {
        const statusTask = project.statusTask || {};
        let tasks = Object.entries(statusTask).flatMap(([status, tasksArr]) =>
            tasksArr.map((task) => ({
                ...task,
                projectId: parentProjectId || project._id,
                subProjectId:
                    project._id !== parentProjectId ? project._id : null,
                status,
            }))
        );

        if (project.subProjects?.length) {
            project.subProjects.forEach((sub) => {
                tasks = tasks.concat(getAllTasks(sub, project._id));
            });
        }

        return tasks;
    }

    let myTasks = allowedProjects.flatMap((project) => {
        return getAllTasks(project).filter(
            (task) =>
                task.assignedEmployees?.some(
                    (emp) => emp.userId === user.userId
                ) || ["Admin", "Management"].includes(user.department)
        );
    });

    if (selectProjectFilter) {
        myTasks = myTasks.filter((task) => {
            const projectName = allowedProjects.find(
                (p) => p._id === task.projectId
            )?.projectName;

            return projectName === selectProjectFilter;
        });
    }

    if (selectEmployee) {
        myTasks = myTasks.filter((task) =>
            task.assignedEmployees?.some(
                (emp) => emp.userId === selectEmployee
            )
        );
    }

    myTasks = myTasks.filter(
        (task) => task.status !== "completed" && task.status !== "complete"
    );

    myTasks = myTasks.sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.createdAt);
        const dateB = new Date(b.updatedAt || b.createdAt);
        return dateB - dateA;
    });

    const getProjectDisplayName = (task, allowedProjects) => {
        const parent = allowedProjects.find(p => p._id === task.projectId);

        if (task.subProjectId && parent?.subProjects?.length) {
            const sub = parent.subProjects.find(
                s => s._id === task.subProjectId
            );

            if (sub) {
                return `${parent.projectName} / ${sub.projectName}`;
            }
        }

        return parent?.projectName || "Unknown Project";
    };

    // Pagination first
    const allGroupedTasks = myTasks.reduce((acc, task) => {
        const projectName = getProjectDisplayName(task, allowedProjects);

        if (!acc[projectName]) {
            acc[projectName] = [];
        }

        acc[projectName].push(task);

        return acc;
    }, {});

    const groupedArray = Object.entries(allGroupedTasks);

    const filteredGroups = groupedArray.filter(([projectName]) =>
        projectName.toLowerCase().includes(search.toLowerCase())
    );

    const totalPages = Math.ceil(filteredGroups.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedGroups = filteredGroups.slice(startIndex, endIndex);

    const availableProjects = [
        ...new Set(
            projects
                .filter((project) => {
                    if (["Admin", "Management"].includes(user.department))
                        return true;
                    return (
                        project.sharedWith?.includes(user.userId) ||
                        project.createdBy === user.username
                    );
                })
                .map((p) => p.projectName)
        ),
    ];

    if (loading) {
        return (
            <div className="p-2 rs:p-6 bg-white shadow-md rounded-lg space-y-3">
                {[...Array(6)].map((_, i) => (
                    <SkeletonPage key={i} />
                ))}
            </div>
        );
    }

    const getIconColorFromStatus = (status, isOverdue) => {
        if (isOverdue) return "#ef4444";

        const bgClass = getStatusColor(status);

        const bg = bgClass.split(" ")[0];

        return tailwindBgToHex[bg] || "#9ca3af";
    };

    return (
        <div className="p-2 rs:p-6 bg-white shadow-md rounded-lg mt-10">
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h2 className="text-[14px] sm:text-[16px] md:text-[20px] font-semibold text-gray-800">
                    My Tasks
                </h2>

                <div className="flex flex-col zy:flex-row gap-2 w-full sm:w-auto">
                    <Input
                        type="search"
                        placeholder="Search Project Name"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="px-2 py-1 text-xs sm:text-sm lg:text-sm lg:px-3 lg:py-2 w-full sm:w-auto"
                    />
                    {(user?.department === "Admin" || user?.department === "Management") && (
                        <select
                            name="projectFilter"
                            id="projectFilter"
                            className="border rounded px-2 py-1 text-xs sm:text-sm lg:text-sm lg:px-3 lg:py-2 w-full sm:w-auto"
                            value={selectEmployee}
                            onChange={(e) => setSelectEmployee(e.target.value)}
                        >
                            <option value="">Employees Task</option>
                            {employees.filter((emp) => emp.departmentName === "Development" || emp.departmentName === "Design")
                                .map((emp) => (
                                    <option key={emp.employeeId} value={emp.employeeId}>
                                        {`${emp.firstName} ${emp.lastName}`}
                                    </option>
                                ))}
                        </select>)}

                    <select
                        name="projectFilter"
                        id="projectFilter"
                        className="border rounded px-2 py-1 text-xs sm:text-sm lg:text-sm lg:px-3 lg:py-2 w-full sm:w-auto"
                        value={selectProjectFilter}
                        onChange={(e) => setSelectProjectFilter(e.target.value)}
                    >
                        <option value="">All Projects</option>
                        {availableProjects.map((name) => (
                            <option key={name} value={name}>
                                {name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="w-full">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="flex items-center px-2 py-1 text-left text-xs font-Bold text-gray-600 uppercase tracking-wider lg:px-3 lg:py-2 lg:text-sm">
                                Projects <ChevronRight size={16} /> Tasks
                            </th>
                            <th className="px-2 py-1 text-left text-xs font-Bold text-gray-600 uppercase tracking-wider lg:px-3 lg:py-2 lg:text-sm hidden xs:table-cell">
                                Status
                            </th>
                            <th className="hidden xs:table-cell px-2 py-1 text-left text-xs font-Bold text-gray-600 uppercase tracking-wider lg:px-3 lg:py-2 lg:text-sm">
                                Priority
                            </th>
                            <th className="px-2 py-1 text-left text-xs font-Bold text-gray-600 uppercase tracking-wider hidden sm:table-cell lg:px-3 lg:py-2 lg:text-sm">
                                Assigned To
                            </th>
                            <th className="px-2 py-1 text-left text-xs font-Bold text-gray-600 uppercase tracking-wider hidden xl:table-cell lg:px-3 lg:py-2 lg:text-sm">
                                Created By
                            </th>
                            <th className="px-2 py-1 text-left text-xs font-Bold text-gray-600 uppercase tracking-wider hidden custom:table-cell lg:px-3 lg:py-2 lg:text-sm">
                                Due Date
                            </th>
                            <th className="px-2 py-1 text-left text-xs font-Bold text-gray-600 uppercase tracking-wider lg:px-3 lg:py-2 lg:text-sm">
                                Actions
                            </th>
                        </tr>
                    </thead>

                    <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedGroups.filter(([projectName]) => projectName.toLowerCase().includes(search.toLowerCase()))
                            .map(([projectName, tasks]) => (
                                <React.Fragment key={projectName}>
                                    <tr className=' border-b  border-gray-200 bg-[#dfeaf6]'>
                                        <td colSpan="7" className=" px-2 rounded-md py-1 lg:px-3 lg:py-2 font-medium text-gray-900 text-lg border-b border-gray-200">
                                            <div className=" flex flex-row items-center sm:items-center gap-2 sm:gap-3">
                                                <span className=" text-black font-semibold px-1 sm:px-3 py-0 sm:py-1 rounded sm:rounded-lg text-[12px] sm:text-[16px]">
                                                    {projectName}
                                                </span>
                                                <span className="text-gray-600 text-xs lg:text-sm">{tasks.length} Tasks</span>
                                            </div>
                                        </td>
                                    </tr>

                                    {tasks.map((task, index) => {
                                        const now = new Date();
                                        const due = task.dueDate
                                            ? new Date(task.dueDate.replace(" ", "T") + "+05:30")
                                            : null;

                                        const isOverdue =
                                            due &&
                                            now > due &&
                                            task.status !== "completed" &&
                                            task.status !== "complete";

                                        return (
                                            <tr
                                                key={task._id}
                                                className="hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-100"
                                                onClick={() =>
                                                    onTaskClick(task.projectId, task.subProjectId, task._id, task.status)
                                                }
                                            >
                                                <td className="px-2 py-1 text-left text-xs lg:text-sm text-gray-600 max-w-[250px]">
                                                    <div className="flex gap-2 items-center">
                                                        <span className={`font-bold ${isOverdue ? "text-red-500" : ""}`}>
                                                            <CircleCheck
                                                                className="w-4 h-4"
                                                                color={getIconColorFromStatus(task.status, isOverdue)}
                                                            />
                                                        </span>
                                                        <span className="break-words">
                                                            {(task?.title || "").length > 20
                                                                ? `${task.title.slice(0, 20)}...`
                                                                : (task.title || "Untitled")}
                                                        </span>
                                                    </div>
                                                </td>

                                                <td className="px-2 py-1 text-left text-xs whitespace-nowrap text-[14px] hidden xs:table-cell">
                                                    <span
                                                        className={`${getStatusColor(task.status)
                                                            } text-white px-2 py-1 rounded`}
                                                    >
                                                        {formatStatusLabel(task.status)}
                                                    </span>
                                                    {isOverdue && (
                                                        <span className="ml-2 px-2 py-[2px] text-xs bg-red-500 text-white border border-red-600 rounded">
                                                            Overdue
                                                        </span>
                                                    )}
                                                </td>

                                                <td className="hidden xs:table-cell px-2 py-1 text-left text-xs whitespace-nowrap lg:text-sm text-gray-600">
                                                    <TaskPriority value={task.priority} showclear={false} />
                                                </td>

                                                <td className="px-2 py-1 text-left text-xs whitespace-nowrap lg:text-sm text-gray-600 hidden sm:table-cell">
                                                    <TaskEmployees
                                                        selected={task.assignedEmployees?.map((e) => e.userId) || []}
                                                        employees={employees || []}
                                                        showDropdown={false}
                                                        hideSelected={true}
                                                    />
                                                </td>

                                                <td className="px-2 py-1 text-left text-xs font-semibold whitespace-nowrap lg:text-sm text-gray-600 hidden  xl:table-cell">
                                                    {task.createdByFullName || "-"}
                                                </td>

                                                <td className="px-2 py-1 text-left text-xs whitespace-nowrap lg:text-sm text-gray-600 hidden custom:table-cell">
                                                    {task.dueDate ? new Date(task.dueDate).toLocaleString() : "-"}
                                                </td>

                                                <td className="px-2 py-1 text-left text-xs whitespace-nowrap lg:text-sm text-gray-600">
                                                    <div
                                                        className="inline-block p-1 rounded cursor-pointer hover:bg-blue-100"
                                                        onClick={() =>
                                                            onTaskClick(task.projectId, task.subProjectId, task._id, task.status)
                                                        }
                                                    >
                                                        <Eye className="w-4 h-4 text-blue-500" />
                                                    </div>
                                                </td>
                                            </tr>)
                                    })}
                                </React.Fragment>
                            ))}

                    </tbody>

                </table>
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    handlePageChange={setCurrentPage}
                />
            </div>
        </div>
    );
}
